import { get, isEqual, isObject } from "lodash";
import { DateFormatOptions, DateTimeFormatOptions, TimeFormatOptions } from "./dateFormat";
import { ListFormatOptions } from "./localeFormat";
import { TMessageKey } from "./types.gen";
import en from "./locales/en/translation.json";
import { DateValue } from "@/utils/Dates";
import { ShareType, ShareTypeFormatOptions } from "@/common/share";
import { FormatNumberOptions, FormatPercentOptions } from "@/utils/numbers";
import { logError } from "@/utils/log";

type AmountParamDef = { type: "amount"; currency?: string };
type AmountParam = AmountParamDef & { value: number | undefined };

type CapitalizeDef = { type: "capitalize" };
type Capitalize = CapitalizeDef & { value: string | TMessage | TMessageParam };

type CountryParamDef = { type: "country" };
type CountryParam = CountryParamDef & { code: string | undefined };

type DateParamDef = { type: "date" } & DateFormatOptions;
type DateParam = DateParamDef & { value: DateValue | undefined };

type DateTimeParamDef = { type: "datetime" } & DateTimeFormatOptions;
type DateTimeParam = DateTimeParamDef & { value: DateValue | undefined };

type ExactAmountParamDef = { type: "exactAmount"; currency: string };
type ExactAmountParam = ExactAmountParamDef & { value: number };

type ListParamDef = { type: "list" } & ListFormatOptions;
type ListParam = ListParamDef & { value: (TMessage | TMessageParam | string | number)[] };

type MdListDef = { type: "md-list" };
type MdList = MdListDef & { value: (TMessage | TMessageParam | string | number)[] };

type NominalValueParamDef = { type: "nominal-value"; currency: string };
type NominalValueParam = NominalValueParamDef & { value: number };

type NumberParamDef = { type: "number" } & FormatNumberOptions;
type NumberParam = NumberParamDef & { value: number | undefined };

type OrgNoParamDef = { type: "org-no" };
type OrgNoParam = OrgNoParamDef & { value: string | undefined };

type PercentParamDef = { type: "percent" } & FormatPercentOptions;
type PercentParam = PercentParamDef & { value: number | undefined };

type PriceParamDef = { type: "price"; currency: string };
type PriceParam = PriceParamDef & { value: number };

type ShareTypeParamDef = { type: "shareType"; options?: ShareTypeFormatOptions };
type ShareTypeParam = ShareTypeParamDef & { value: ShareType };

type SsnParamDef = { type: "ssn"; options?: { nationality?: string } };
type SsnParam = SsnParamDef & { value: string };

type TextParamDef = { type: "text"; params?: TMessageParams };
type TextParam = TextParamDef & { value: string };

type TimeParamDef = { type: "time" } & TimeFormatOptions;
type TimeParam = TimeParamDef & { value: DateValue | undefined };

type UncapitilizeDef = { type: "uncapitilize" };
type Uncapitilize = UncapitilizeDef & { value: string | TMessage | TMessageParam };

type WeekdayParamDef = { type: "weekday" };
type WeekdayParam = WeekdayParamDef & { value: DateValue | undefined };

type YearParamDef = { type: "year" };
type YearParam = YearParamDef & { value: DateValue | undefined };

type YearMonthParamDef = { type: "year-month" };
type YearMonthParam = YearMonthParamDef & { value: DateValue | undefined };

export type TMessageParamDef =
  | AmountParamDef
  | CapitalizeDef
  | CountryParamDef
  | DateParamDef
  | DateTimeParamDef
  | ExactAmountParamDef
  | ListParamDef
  | MdListDef
  | NominalValueParamDef
  | NumberParamDef
  | OrgNoParamDef
  | PercentParamDef
  | PriceParamDef
  | ShareTypeParamDef
  | SsnParamDef
  | TextParamDef
  | TimeParamDef
  | UncapitilizeDef
  | WeekdayParamDef
  | YearParamDef
  | YearMonthParamDef;

export type TMessageParam =
  | AmountParam
  | Capitalize
  | CountryParam
  | DateParam
  | DateTimeParam
  | ExactAmountParam
  | ListParam
  | MdList
  | NominalValueParam
  | NumberParam
  | OrgNoParam
  | PercentParam
  | PriceParam
  | ShareTypeParam
  | SsnParam
  | TextParam
  | TimeParam
  | Uncapitilize
  | WeekdayParam
  | YearParam
  | YearMonthParam;

const paramTypes: Record<TMessageParamDef["type"], true> = {
  ["amount"]: true,
  ["capitalize"]: true,
  ["country"]: true,
  ["date"]: true,
  ["datetime"]: true,
  ["exactAmount"]: true,
  ["list"]: true,
  ["md-list"]: true,
  ["number"]: true,
  ["nominal-value"]: true,
  ["org-no"]: true,
  ["percent"]: true,
  ["price"]: true,
  ["ssn"]: true,
  ["shareType"]: true,
  ["text"]: true,
  ["time"]: true,
  ["uncapitilize"]: true,
  ["weekday"]: true,
  ["year"]: true,
  ["year-month"]: true,
};

// @ts-expect-error n/a
export const isTMessageParam = (o: unknown): o is TMessageParam => paramTypes[get(o, "type")];

export type TMessageParams = {
  [key: string]:
    | TMessageParam
    | TMessage
    | number
    | string
    | null
    | undefined
    | Array<TMessageParam | TMessage | number | string | null | undefined>;
};

export type TMessage = { key: TMessageKey; params?: TMessageParams } | { translated: string };

export type TFunction = (
  keyOrMsg: TMessageKey | TMessage | TMessageParam,
  params?: TMessageParams,
) => string;

export function isTranslatedMessage(o: unknown): o is { translated: string } {
  return isObject(o) && "translated" in o && typeof o.translated === "string";
}

export function isTMessage(o: unknown): o is TMessage {
  if (o === null || o === undefined || typeof o !== "object") {
    return false;
  }
  const keys = Object.keys(o).sort();
  return (
    isEqual(keys, ["translated"]) || isEqual(keys, ["key"]) || isEqual(keys, ["key", "params"])
  );
}

const isMessageKey = (key: string): key is TMessageKey => key in en;

export const brregMessage = (brMessage: string): TMessage => {
  const key = `brreg.${brMessage}`;
  if (isMessageKey(key)) return { key };
  logError(`Missing translation for Brønnøysund text "${brMessage}"`);
  return { translated: brMessage };
};
